import { DateTime } from 'luxon';
import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useAccessForItem from '../hooks/useAccessForItem';
import useGetGeometryParamForItem from '../hooks/useGetGeometryParamForItem';
import useStatusEditProject from '../hooks/useStatusEditProject';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantDraggable } from '../store/slices/gantDraggableSlice';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { ItemType } from '../types';
import GantGridListArrow from './gantGridListArrow';
import GantItemParent from './gantItemParent';
import GantItemSimple from './gantItemSimple';

interface GantItemProps {
    item: ItemType;
    // itemAsJson: string; //ItemType;
}

const GantItem: React.FC<GantItemProps> = ({ item }) => {
    // const item = JSON.parse(itemAsJson);
    const dispatch = useDispatch<ComponentsDispatch>();

    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);
    const isDragging = useSelector<RootState, boolean>((state) => state.gantDraggable.isDragging);

    const currentItemVerticalDraggable = useSelector<RootState, ItemType | null>(
        (state) => state.gantDraggable.currentItemVerticalDraggable,
    );
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const clickCounter = useRef<number>(0);

    const paramForItem = useGetGeometryParamForItem(item);
    const offsetY = paramForItem.startY;
    const offsetX = paramForItem.startX;
    const gantItemRef = useRef<SVGRectElement | null>(null);
    const [isOverMouse, setIsOverMouse] = useState(false);

    const { days } = useMemo(() => {
        const start = DateTime.fromISO(item.startDate);
        const end = DateTime.fromISO(item.endDate);
        const days = Math.ceil(end.diff(start, 'days').days) + 1;
        return { start, end, days };
    }, [item.startDate, item.endDate]);

    const widthItem = useMemo(() => days * widthColumn, [days, widthColumn]);

    const handleMouseDown = (e: React.MouseEvent<SVGRectElement>) => {
        if (e.target === gantItemRef.current && !clickCounter.current) {
            dispatch(setterGantDraggable({ dragging: 'both' }));
            dispatch(setterGantItems({ currentGantItem: item }));
            timeoutRef.current = setTimeout(() => {
                dispatch(setterGantItems({ isItemEdit: false }));
            }, 100);
        }
    };

    const handleClick = () => {
        // imitation double click
        clickCounter.current += 1;

        if (clickCounter.current === 2) {
            dispatch(setterGantItems({ isItemEdit: true, currentGantItem: item }));
        }

        setTimeout(() => {
            clickCounter.current = 0;
        }, 300);
    };

    const isEditableProject = useStatusEditProject(item.projectId);

    const accessView = useAccessForItem(item, [
        'work-item.all.view',
        'work-item.owner.view',
        'work-item.assignee.view',
    ]);
    const accessEdit = useAccessForItem(item, [
        'work-item.all.edit',
        'work-item.owner.edit',
        'work-item.assignee.edit',
        'work-item.all.create',
        'work-item.owner.create',
        'work-item.assignee.create',
        'work-item.all.manage',
        'work-item.owner.manage',
        'work-item.assignee.manage',
    ]);

    const propsPath: React.SVGProps<SVGPathElement & SVGRectElement> = {
        ref: gantItemRef,
        cursor: accessView ? 'pointer' : undefined,
        onMouseDown: accessEdit && isEditableProject ? handleMouseDown : undefined,
        onMouseUp: accessEdit
            ? () => {
                  if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                  }
              }
            : undefined,
        onClick: accessView ? handleClick : undefined,
        strokeWidth: 1,
        // stroke: 'white',
        // filter: 'url(#shadow)',
    };

    const handleOverMouse = () => {
        if (!isDragging) {
            setIsOverMouse(true);
        }
    };

    const handleOutMouse = () => {
        setIsOverMouse(false);
    };

    return (
        <g
            key={item.id}
            transform={`translate(0, ${offsetY})`}
            opacity={currentItemVerticalDraggable?.id === item.id ? 0.3 : 1}
        >
            <GantGridListArrow item={item} />
            <g
                onMouseOver={accessView ? handleOverMouse : undefined}
                onMouseOut={accessView ? handleOutMouse : undefined}
            >
                {item.children?.length ? (
                    <GantItemParent
                        item={item}
                        x={offsetX}
                        widthItem={widthItem}
                        propsPath={propsPath}
                        isOverMouse={isOverMouse}
                        accessView={accessView}
                        accessEdit={accessEdit}
                    />
                ) : (
                    <GantItemSimple
                        isOverMouse={isOverMouse}
                        item={item}
                        x={offsetX}
                        widthItem={widthItem}
                        onOverMouse={handleOverMouse}
                        propsPath={propsPath}
                        accessView={accessView}
                        accessEdit={accessEdit}
                    />
                )}
            </g>
        </g>
    );
};

export default React.memo(GantItem, (prevProps, nextProps) => {
    return (
        prevProps.item.id === nextProps.item.id &&
        // prevProps.offsetY === nextProps.offsetY &&
        // prevProps.offsetX === nextProps.offsetX &&
        prevProps.item.completionPercentage === nextProps.item.completionPercentage &&
        prevProps.item.startDate === nextProps.item.startDate &&
        prevProps.item.endDate === nextProps.item.endDate &&
        prevProps.item.children === nextProps.item.children &&
        prevProps.item.parentId === nextProps.item.parentId &&
        prevProps.item.color === nextProps.item.color &&
        prevProps.item.bondRight === nextProps.item.bondRight &&
        prevProps.item.bondLeft === nextProps.item.bondLeft &&
        prevProps.item.tasks === nextProps.item.tasks &&
        prevProps.item.idIndex === nextProps.item.idIndex &&
        prevProps.item.assignee === nextProps.item.assignee &&
        prevProps.item.ownerItemId === nextProps.item.ownerItemId &&
        prevProps.item.bondSE === nextProps.item.bondSE &&
        prevProps.item.bondSS === nextProps.item.bondSS &&
        prevProps.item.bondES === nextProps.item.bondES &&
        prevProps.item.bondEE === nextProps.item.bondEE &&
        prevProps.item.bondParentSE === nextProps.item.bondParentSE &&
        prevProps.item.bondParentSS === nextProps.item.bondParentSS &&
        prevProps.item.bondParentES === nextProps.item.bondParentES &&
        prevProps.item.bondParentEE === nextProps.item.bondParentEE &&
        prevProps.item.hideForCollapse === nextProps.item.hideForCollapse
    );
});
